import React, { useMemo, useState } from 'react';
import { RefreshCw, Download } from 'lucide-react';
import ChartContainer from './ChartContainer';
import DashboardTabHeader from './shared/DashboardTabHeader';
import OfficialTrainingReport from './OfficialTrainingReport';
import SortableHeader, { usePagedSortableData, TablePager } from './SortableTable';
import TrainingStatCards from './lnd/TrainingStatCards';
import { exportSheetsToExcel } from '../utils/excelExport';
import { useAuth } from '../contexts/AuthContext';
import { computeTrainingStats } from '../utils/trainingStats';
import { exportExcelBtnCls } from './shared/ExportExcelButton';

// The summary table's grouping key.
// Order = the toggle order; the first is the default.
const GROUP_BY_OPTIONS = [
  { key: 'designation', label: 'Designation' },
  { key: 'department', label: 'Department' },
  { key: 'grade', label: 'Grade' },
];

/** The grouping switch. Rendered on both the chart and the table below it, on
 *  one piece of state — the chart IS the table's Avg Hours column, so letting
 *  the two sit on different groupings would only invite reading one as the
 *  other. */
const GroupByToggle = ({ value, onChange }) => (
  <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
    {GROUP_BY_OPTIONS.map((opt) => (
      <button
        key={opt.key}
        type="button"
        onClick={() => onChange(opt.key)}
        className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${
          value === opt.key
            ? 'bg-white text-blue-600 shadow-sm'
            : 'text-gray-600 hover:text-gray-900'
        }`}
      >
        {opt.label}
      </button>
    ))}
  </div>
);

/** Hours to one decimal, as a number — a float sum reaches the chart as
 *  948.6999999999999 otherwise. */
const round1 = (hours) => Number((hours || 0).toFixed(1));

/**
 * The Training Hours tab. Rows, period, loading and refresh all come from
 * LDDashboard: if this tab fetched the same two endpoints
 * again and kept its own date range, the Overview tab and this one could
 * show different periods — and different numbers. Don't.
 */
const TrainingHoursAggregate = ({
  attendance = [],
  cpdRecords = [],
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
  loading = false,
  lastUpdated = null,
  onRefresh,
}) => {
  const { hasPermission } = useAuth();
  const [groupBy, setGroupBy] = useState(GROUP_BY_OPTIONS[0].key);
  const groupLabel = (GROUP_BY_OPTIONS.find((o) => o.key === groupBy) || GROUP_BY_OPTIONS[0]).label;

  // One aggregation for the whole tab — hour buckets, per-designation summary,
  // participants and trainer hours all come out of the same call the Overview
  // tab makes (utils/trainingStats.js), so the two can never disagree again.
  const stats = useMemo(
    () => computeTrainingStats(attendance, cpdRecords, startDate, endDate),
    [attendance, cpdRecords, startDate, endDate]
  );
  const { filteredAttendance, filteredCpdRecords, trainer: trainerStats } = stats;

  const {
    rows: trainerPage,
    sortConfig: trainerSort,
    requestSort: requestTrainerSort,
    page: trainerPageNo,
    setPage: setTrainerPage,
    total: trainerTotal,
  } = usePagedSortableData(trainerStats.people, { key: 'trainerHours', direction: 'desc' });

  // Summary table — same rows aggregated by whichever key is selected.
  const summaryRows = stats.summaries[groupBy] || stats.summaries.designation;
  const {
    rows: summaryPage,
    sortConfig: summarySort,
    requestSort: requestSummarySort,
    page: summaryPageNo,
    setPage: setSummaryPage,
    total: summaryTotal,
  } = usePagedSortableData(summaryRows);

  // Participants: pages through
  // everyone 30 at a time — sorting applies to the
  // whole set, so page 1 is the top PAGE_SIZE by whatever column you picked. Opens on
  // Total Hours descending, so #1 is the top performer.
  const {
    rows: participantPage,
    sortConfig: participantSort,
    requestSort: requestParticipantSort,
    page: participantPageNo,
    setPage: setParticipantPage,
    total: participantTotal,
    pageSize: participantPageSize,
  } = usePagedSortableData(stats.peopleArray, { key: 'totalHours', direction: 'desc' });

  // Excel: the summary (in whichever grouping is on screen) and the full
  // participant list (not just the page shown — the export is what leaves the
  // building).
  const handleExport = () => {
    const period = `${startDate || 'All time'} to ${endDate || 'Present'}`;
    const summary = summaryRows.map(row => ({
      'Period': period,
      [groupLabel]: row.group,
      'People': row.count,
      'Total Hours': Number(row.totalHours.toFixed(1)),
      'Internal Mandatory Hours': Number(row.mandatoryHours.toFixed(1)),
      'Internal Non-mandatory Hours': Number(row.nonMandatoryHours.toFixed(1)),
      'External Hours': Number(row.externalHours.toFixed(1)),
      'Avg Hours': Number(row.avgHours.toFixed(1)),
    }));
    const participants = stats.peopleArray
      .slice()
      .sort((a, b) => b.totalHours - a.totalHours)
      .map(p => ({
        'Period': period,
        'Name': p.name,
        'Email': p.email,
        'Designation': p.position || 'Unknown',
        'Department': p.department || 'Unknown',
        'Grade': p.grade || 'Unknown',
        'Total Hours': Number(p.totalHours.toFixed(1)),
        'Internal Mandatory Hours': Number(p.mandatoryHours.toFixed(1)),
        'Internal Non-mandatory Hours': Number(p.nonMandatoryHours.toFixed(1)),
        'External Hours': Number(p.externalHours.toFixed(1)),
        'Internal Sessions': p.internalSessions,
        'External Sessions': p.externalSessions,
      }));
    // Trainer hours ride along as their own sheet — they are nobody's
    // training hours, so they must not be mixed into the two above.
    const trainers = trainerStats.people
      .slice()
      .sort((a, b) => b.trainerHours - a.trainerHours)
      .map(t => ({
        'Period': period,
        'Trainer': t.name,
        'Email': t.email,
        'Designation': t.position || 'Unknown',
        'Department': t.department || 'Unknown',
        'Sessions Taught': t.sessions,
        'As Lead': t.leadSessions,
        'As Assistant': t.assistantSessions,
        'Trainer Hours': Number(t.trainerHours.toFixed(1)),
      }));
    exportSheetsToExcel(
      [
        { name: `By ${groupLabel}`, rows: summary },
        { name: 'Participants', rows: participants },
        { name: 'Trainer Hours', rows: trainers },
      ],
      'Training_Hours_Report.xlsx'
    );
  };

  if (loading && attendance.length === 0) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 animate-spin text-blue-600 mx-auto mb-2" />
          <p className="text-gray-600">Loading training hours data...</p>
        </div>
      </div>
    );
  }

  // A failed fetch is reported once, by the page-level notice in LDDashboard —
  // this tab no longer owns the request, so it no longer owns the error.

  return (
    <div className="space-y-6">
      {/* Header — unified tab header (docs/dashboard-ui.md) */}
      <DashboardTabHeader
        description="Aggregate data from internal sessions and External CPD records"
        lastUpdated={lastUpdated}
        actions={
          <>
            <button
              onClick={onRefresh}
              disabled={loading}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
            {hasPermission('ld:view') && (
              <button
                onClick={handleExport}
                disabled={stats.peopleArray.length === 0}
                className={exportExcelBtnCls}
              >
                <Download className="w-4 h-4" />
                Export Excel
              </button>
            )}
          </>
        }
      />

      {/* Date Range Filter */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex flex-wrap items-center gap-4">
          <label className="text-sm font-medium text-gray-700">Date Range:</label>
          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-600">From:</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => onStartDateChange(e.target.value)}
              className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-600">To:</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => onEndDateChange(e.target.value)}
              className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          {(startDate || endDate) && (
            <button
              onClick={() => {
                onStartDateChange('');
                onEndDateChange('');
              }}
              className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
            >
              Clear Filters
            </button>
          )}
          <div className="text-sm text-gray-500 ml-auto">
            Showing {filteredAttendance.length} attendance + {filteredCpdRecords.length} External CPD records
          </div>
        </div>
      </div>

      {/* High-level Stats — the very same component the Overview tab renders,
          so the two cannot look like different reports. */}
      <TrainingStatCards stats={stats} />

      {/* Official Report Card - External Audit Format. Raw arrays, not the
          filtered ones: the report filters internally and needs last year's
          rows for its year-on-year comparison. */}
      <OfficialTrainingReport
        attendance={attendance}
        cpdRecords={cpdRecords}
        startDate={startDate}
        endDate={endDate}
      />

      {/* Three-bucket breakdown: the buckets are viewed
          independently — only Internal Mandatory counts towards the target. */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Training Hours Breakdown</h3>
        <ChartContainer
          title="Internal Mandatory vs Non-mandatory vs External"
          data={{
            'Internal Mandatory': round1(stats.totalMandatoryHours),
            'Internal Non-mandatory': round1(stats.totalNonMandatoryHours),
            'External CPD': round1(stats.totalExternalHours)
          }}
          type="doughnut"
          height={300}
        />
      </div>

      {/* Top 10 Performers — ahead of the grouped views. */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Top 10 Performers by Total Training Hours
        </h3>
        <ChartContainer
          title="Top 10 Performers"
          data={Object.fromEntries(
            stats.topPerformers.map(p => [p.name, round1(p.totalHours)])
          )}
          type="bar"
          height={400}
          backgroundColor="#3B82F680"
          borderColor="#3B82F6"
        />
      </div>

      {/* Average Hours by the selected grouping — the chart of the summary
          table's Avg Hours column, so it follows the same toggle.
          Switching from either control moves both. */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <h3 className="text-lg font-semibold text-gray-900">
            Average Training Hours by {groupLabel}
          </h3>
          <GroupByToggle value={groupBy} onChange={setGroupBy} />
        </div>
        <ChartContainer
          title={`Avg Hours per ${groupLabel}`}
          data={Object.fromEntries(summaryRows.map(row => [row.group, round1(row.avgHours)]))}
          type="bar"
          height={400}
        />
      </div>

      {/* Summary Table — grouped by designation, department or grade.
          The rows are the same participants either way; only the
          bucket changes, so the totals always tie back to the table below. */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <h3 className="text-lg font-semibold text-gray-900">{groupLabel} Summary</h3>
          <GroupByToggle value={groupBy} onChange={setGroupBy} />
        </div>
        <div className="table-scroll">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <SortableHeader label={groupLabel} sortKey="group" sortConfig={summarySort} onSort={requestSummarySort} className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider" />
                <SortableHeader label="# People" sortKey="count" sortConfig={summarySort} onSort={requestSummarySort} align="right" className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider" />
                <SortableHeader label="Total Hours" sortKey="totalHours" sortConfig={summarySort} onSort={requestSummarySort} align="right" className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider" />
                <SortableHeader label="Mandatory" sortKey="mandatoryHours" sortConfig={summarySort} onSort={requestSummarySort} align="right" className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider" />
                <SortableHeader label="Non-mandatory" sortKey="nonMandatoryHours" sortConfig={summarySort} onSort={requestSummarySort} align="right" className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider" />
                <SortableHeader label="External" sortKey="externalHours" sortConfig={summarySort} onSort={requestSummarySort} align="right" className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider" />
                <SortableHeader label="Avg Hours" sortKey="avgHours" sortConfig={summarySort} onSort={requestSummarySort} align="right" className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider" />
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {summaryPage.map((row) => (
                <tr key={row.group} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {row.group}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {row.count}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900">
                    {row.totalHours.toFixed(1)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-blue-600">
                    {row.mandatoryHours.toFixed(1)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-indigo-600">
                    {row.nonMandatoryHours.toFixed(1)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-green-600">
                    {row.externalHours.toFixed(1)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-purple-600">
                    {row.avgHours.toFixed(1)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <TablePager page={summaryPageNo} setPage={setSummaryPage} total={summaryTotal} />
      </div>

      {/* Participants Detail Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Participants Detail ({participantTotal})</h3>
        <div className="table-scroll">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <SortableHeader label="Rank" sortable={false} sortConfig={participantSort} onSort={requestParticipantSort} className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider" />
                <SortableHeader label="Name" sortKey="name" sortConfig={participantSort} onSort={requestParticipantSort} className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider" />
                <SortableHeader label="Designation" sortKey="position" sortConfig={participantSort} onSort={requestParticipantSort} className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider" />
                <SortableHeader label="Total Hours" sortKey="totalHours" sortConfig={participantSort} onSort={requestParticipantSort} align="right" className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider" />
                <SortableHeader label="Mandatory" sortKey="mandatoryHours" sortConfig={participantSort} onSort={requestParticipantSort} align="right" className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider" />
                <SortableHeader label="Non-mandatory" sortKey="nonMandatoryHours" sortConfig={participantSort} onSort={requestParticipantSort} align="right" className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider" />
                <SortableHeader label="External" sortKey="externalHours" sortConfig={participantSort} onSort={requestParticipantSort} align="right" className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider" />
                <SortableHeader label="Sessions" sortKey="sessions" sortConfig={participantSort} onSort={requestParticipantSort} accessor={r => r.internalSessions + r.externalSessions} className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider" />
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {participantPage.map((person, idx) => (
                <tr key={person.email} className="hover:bg-gray-50">
                  {/* Rank counts through the pages, not within one. */}
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900">
                    #{(participantPageNo - 1) * participantPageSize + idx + 1}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{person.name}</div>
                    <div className="text-sm text-gray-500">{person.email}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {person.position || 'N/A'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900">
                    {person.totalHours.toFixed(1)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-blue-600">
                    {person.mandatoryHours.toFixed(1)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-indigo-600">
                    {person.nonMandatoryHours.toFixed(1)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-green-600">
                    {person.externalHours.toFixed(1)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {person.internalSessions + person.externalSessions}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <TablePager page={participantPageNo} setPage={setParticipantPage} total={participantTotal} />
      </div>

      {/* Trainer Hours — teaching, which earns no training hours and therefore
          appears in none of the tables above. One row per
          trainer, from the rows written when a session is marked completed. */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex flex-wrap items-baseline justify-between gap-2 mb-1">
          <h3 className="text-lg font-semibold text-gray-900">Trainer Hours ({trainerTotal})</h3>
          {trainerStats.totalHours > 0 && (
            <p className="text-sm text-gray-500">
              {trainerStats.totalSessions} session{trainerStats.totalSessions === 1 ? '' : 's'} taught
              {' · '}{trainerStats.totalHours.toFixed(1)} trainer hours in total
            </p>
          )}
        </div>
        <p className="text-xs text-gray-500 mb-4">
          Session hours × each trainer&apos;s own factor, booked when a session is marked completed.
          Teaching does not count towards the trainer&apos;s own mandatory target, so these hours are
          kept separate from everything above. Sessions Taught counts lead and assistant seats alike.
        </p>
        {trainerStats.people.length === 0 ? (
          <p className="text-sm text-gray-500 py-6 text-center">
            No trainer hours in this period — sessions record them once they are marked completed.
          </p>
        ) : (
          <>
            <div className="table-scroll">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <SortableHeader label="Trainer" sortKey="name" sortConfig={trainerSort} onSort={requestTrainerSort} className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider" />
                    <SortableHeader label="Designation" sortKey="position" sortConfig={trainerSort} onSort={requestTrainerSort} className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider" />
                    <SortableHeader label="Department" sortKey="department" sortConfig={trainerSort} onSort={requestTrainerSort} className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider" />
                    <SortableHeader label="Sessions Taught" sortKey="sessions" sortConfig={trainerSort} onSort={requestTrainerSort} align="right" className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider" />
                    <SortableHeader label="As Lead" sortKey="leadSessions" sortConfig={trainerSort} onSort={requestTrainerSort} align="right" className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider" />
                    <SortableHeader label="As Assistant" sortKey="assistantSessions" sortConfig={trainerSort} onSort={requestTrainerSort} align="right" className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider" />
                    <SortableHeader label="Trainer Hours" sortKey="trainerHours" sortConfig={trainerSort} onSort={requestTrainerSort} align="right" className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider" />
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {trainerPage.map((t) => (
                    <tr key={t.email} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{t.name}</div>
                        <div className="text-sm text-gray-500">{t.email}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{t.position || 'N/A'}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{t.department || 'N/A'}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-500">{t.sessions}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-500">{t.leadSessions}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-500">{t.assistantSessions}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-bold text-purple-700">
                        {t.trainerHours.toFixed(1)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <TablePager page={trainerPageNo} setPage={setTrainerPage} total={trainerTotal} />
          </>
        )}
      </div>
    </div>
  );
};

export default TrainingHoursAggregate;
